"use client";

import Image from "next/image";
import Link from "next/link";

import type { ArticleCard } from "@/components/shared/article-archive-types";
import { getAlt } from "@/lib/image";
import { sanityImageUrlWithDocumentVersion, urlForImage } from "@/sanity/lib/image";

const accent = "text-[#ff5c00]";

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-NZ", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function ArticleArchiveCard({ post, basePath }: { post: ArticleCard; basePath: string }) {
  const imageUrl =
    post.mainImage && post.mainImage.asset
      ? sanityImageUrlWithDocumentVersion(
          urlForImage(post.mainImage).width(960).quality(85).url(),
          post._updatedAt,
        )
      : null;

  return (
    <article className="group flex h-full min-h-126 flex-col overflow-hidden rounded-2xl border border-[#231f20]/10 bg-white shadow-sm transition-shadow hover:shadow-md md:min-h-136 lg:min-h-144">
      <Link
        href={`${basePath}/${post.slug}`}
        className="flex h-full flex-col focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5c00]"
      >
        {imageUrl ? (
          <div className="relative aspect-video w-full overflow-hidden bg-[#f5f2f0]">
            <Image
              src={imageUrl}
              alt={getAlt(post.mainImage, post.title)}
              fill
              className="object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        ) : null}
        <div className="flex flex-1 flex-col p-6 md:p-8">
          {post.categories?.length ? (
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>
              {post.categories.map((c, i) => (
                <span key={c.slug ?? `card-cat-${i}`}>
                  {i > 0 ? <span className="font-normal text-[#231f20]/35"> · </span> : null}
                  {c.title}
                </span>
              ))}
            </p>
          ) : null}
          <h2 className="mt-2 text-xl font-semibold tracking-tight text-[#231f20] md:text-2xl capitalize">
            {post.title}
          </h2>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[#231f20]/75 md:text-base first-letter:uppercase">
            {post.excerpt}
          </p>
          <p className="mt-auto pt-3 text-xs text-[#231f20]/50">{formatDate(post.publishedAt)}</p>
        </div>
      </Link>
    </article>
  );
}
